/* eslint-disable max-lines */
import { focusManager, onlineManager } from "@tanstack/react-query"

import type { DurableChatTurnConnectionState } from "./durable-chat-turn-subscription"
import type {
  DurableChatTurnEvent,
  DurableChatTurnIdentity,
  DurableChatTurnSnapshot,
  DurableChatTurnStatusResponse,
  DurableChatTurnSubscriptionMessage,
} from "@/api/models"

import {
  defaultCreateSubscription,
  defaultLoadStatus,
  type DurableChatTurnRegistryDependencies,
  type DurableChatTurnRegistryRegistration,
  type DurableChatTurnRegistryState,
  type DurableChatTurnSubscriptionFactory,
  type DurableChatTurnSubscriptionLike,
  type LoadStatus,
  type RegistryEntry,
} from "./durable-chat-turn-registry-types"
import {
  cloneRegistryState,
  computeBackoffDelayMs,
  DEFAULT_BASE_RECOVERY_DELAY_MS,
  DEFAULT_MAX_BUFFERED_EVENTS,
  DEFAULT_MAX_RECOVERY_DELAY_MS,
  DEFAULT_MAX_RETAINED_EVENTS,
  DEFAULT_STATUS_POLL_INTERVAL_MS,
  foldEventOntoState,
  isTerminalStatus,
  matchesTarget,
  replacedRegistryState,
  snapshotsEqual,
  statusWithPendingControls,
  targetKey,
} from "./durable-chat-turn-registry-utils"
import {
  DEFAULT_MAX_RETAINED_TERMINAL_TURNS,
  terminalEntriesToDispose,
} from "./durable-chat-turn-retention"

export type {
  DurableChatTurnRegistryRegistration,
  DurableChatTurnRegistryState,
  DurableChatTurnSubscriptionFactory,
  DurableChatTurnSubscriptionLike,
} from "./durable-chat-turn-registry-types"

export class DurableChatTurnRegistry {
  private readonly baseRecoveryDelayMs: number
  private readonly createSubscription: DurableChatTurnSubscriptionFactory
  private readonly entries = new Map<string, RegistryEntry>()
  private readonly loadStatus: LoadStatus
  private readonly maxBufferedEvents: number
  private readonly maxRecoveryDelayMs: number
  private readonly maxRetainedEvents: number
  private readonly maxRetainedTerminalTurns: number
  private readonly statusPollIntervalMs: number

  constructor(dependencies: DurableChatTurnRegistryDependencies = {}) {
    this.baseRecoveryDelayMs = dependencies.baseRecoveryDelayMs ?? DEFAULT_BASE_RECOVERY_DELAY_MS
    this.createSubscription = dependencies.createSubscription ?? defaultCreateSubscription
    this.loadStatus = dependencies.loadStatus ?? defaultLoadStatus
    this.maxBufferedEvents = dependencies.maxBufferedEvents ?? DEFAULT_MAX_BUFFERED_EVENTS
    this.maxRecoveryDelayMs = dependencies.maxRecoveryDelayMs ?? DEFAULT_MAX_RECOVERY_DELAY_MS
    this.maxRetainedEvents = dependencies.maxRetainedEvents ?? DEFAULT_MAX_RETAINED_EVENTS
    this.maxRetainedTerminalTurns =
      dependencies.maxRetainedTerminalTurns ?? DEFAULT_MAX_RETAINED_TERMINAL_TURNS
    this.statusPollIntervalMs = dependencies.statusPollIntervalMs ?? DEFAULT_STATUS_POLL_INTERVAL_MS

    if (!Number.isSafeInteger(this.maxBufferedEvents) || this.maxBufferedEvents < 1) {
      throw new RangeError("maxBufferedEvents must be a positive safe integer")
    }
    if (!Number.isSafeInteger(this.maxRetainedEvents) || this.maxRetainedEvents < 1) {
      throw new RangeError("maxRetainedEvents must be a positive safe integer")
    }
    if (!Number.isSafeInteger(this.maxRetainedTerminalTurns) || this.maxRetainedTerminalTurns < 1) {
      throw new RangeError("maxRetainedTerminalTurns must be a positive safe integer")
    }
    if (!Number.isSafeInteger(this.baseRecoveryDelayMs) || this.baseRecoveryDelayMs < 1) {
      throw new RangeError("baseRecoveryDelayMs must be a positive safe integer")
    }
    if (
      !Number.isSafeInteger(this.maxRecoveryDelayMs) ||
      this.maxRecoveryDelayMs < this.baseRecoveryDelayMs
    ) {
      throw new RangeError("maxRecoveryDelayMs must be at least baseRecoveryDelayMs")
    }
    if (!Number.isSafeInteger(this.statusPollIntervalMs) || this.statusPollIntervalMs < 1) {
      throw new RangeError("statusPollIntervalMs must be a positive safe integer")
    }
  }

  register(registration: DurableChatTurnRegistryRegistration): DurableChatTurnRegistryState {
    const target: DurableChatTurnIdentity = {
      sessionId: registration.sessionId,
      turnId: registration.turnId,
      epoch: registration.epoch,
    }
    if (!matchesTarget(target, registration.snapshot)) {
      throw new Error("Durable turn snapshot identity does not match registration")
    }

    const key = targetKey(target)
    const existing = this.entries.get(key)
    if (existing) {
      existing.getToken = registration.getToken
      // A non-authoritative re-registration (every current caller — see the
      // field's doc comment) carries a synthesized, EMPTY snapshot. Treating
      // it as real would blank what's already reconstructed and jump
      // `cursor` past events the subscription is mid-replay of, silently
      // dropping them (issue #2513). Idempotent no-op beyond the token
      // refresh above — the subscription and `recover()` own advancing this.
      if (!registration.authoritative) return cloneRegistryState(existing.state)
      if (
        registration.snapshot.snapshotSeq < existing.state.cursor ||
        (existing.state.terminal && registration.snapshot.status !== existing.state.status) ||
        (registration.snapshot.status === existing.state.status &&
          snapshotsEqual(registration.snapshot, existing.state.snapshot))
      ) {
        return cloneRegistryState(existing.state)
      }

      const wasTerminal = existing.state.terminal
      this.replaceFromSnapshot(existing, registration.snapshot)
      if (existing.state.terminal) {
        this.finishTerminalTransition(existing, wasTerminal)
      } else {
        this.drainBufferedEvents(existing)
      }
      this.emit(existing)
      return cloneRegistryState(existing.state)
    }

    // A non-authoritative fresh registration (every current caller — see
    // `authoritative`'s doc comment) never seeds `cursor` from the
    // synthesized snapshot's `snapshotSeq`. That value is the TIP, not a
    // covering point: seeding there would make the subscription open at
    // `afterSeq=<tip>` and the viewer would only ever see events produced
    // AFTER this attach, structurally unable to render anything already
    // produced (issue #2513 finding 2). Seeding at `0` instead lets the
    // backend's own replay/gap machinery reconstruct the full answer: a
    // short/unpruned turn replays every event from the start, a long/pruned
    // one reports `gap=true` and hands back the real covering snapshot via
    // the existing `recover()` gap path.
    const cursor = registration.authoritative ? registration.snapshot.snapshotSeq : 0
    const state: DurableChatTurnRegistryState = {
      ...target,
      cursor,
      events: [],
      reconnecting: false,
      snapshot: registration.snapshot,
      status: registration.snapshot.status,
      terminal: isTerminalStatus(registration.snapshot.status),
    }
    const entry = {
      bufferedEvents: new Map<number, DurableChatTurnEvent>(),
      getToken: registration.getToken,
      listeners: new Set<(state: DurableChatTurnRegistryState) => void>(),
      recovery: null,
      recoveryAttempts: 0,
      recoveryTimer: null,
      state,
      statusPolling: false,
      subscription: undefined as unknown as DurableChatTurnSubscriptionLike,
      subscriptionActive: false,
    }
    entry.subscription = this.createSubscription({
      ...target,
      getAfterSeq: () => entry.state.cursor,
      getToken: () => entry.getToken(),
      onConnectionStateChange: (connectionState) => {
        this.updateConnectionState(entry, connectionState)
      },
      onError: () => {
        // Connection loss is recoverable transport state. The subscription owns
        // reconnect/backoff; the authoritative turn status remains unchanged.
      },
      onMessage: (message) => {
        void this.receive(target, message)
      },
      onReconnectExhausted: () => this.startStatusPolling(entry),
    })
    this.entries.set(key, entry)
    if (state.terminal) {
      this.pruneTerminalEntries()
    } else {
      entry.subscriptionActive = true
      entry.subscription.start()
    }
    return cloneRegistryState(state)
  }

  get(target: DurableChatTurnIdentity): DurableChatTurnRegistryState | undefined {
    const entry = this.entries.get(targetKey(target))
    return entry ? cloneRegistryState(entry.state) : undefined
  }

  subscribe(
    target: DurableChatTurnIdentity,
    listener: (state: DurableChatTurnRegistryState) => void
  ): () => void {
    const entry = this.entries.get(targetKey(target))
    if (!entry) throw new Error("Durable turn is not registered")
    entry.listeners.add(listener)
    // Listener lifetime is only a UI concern. Detaching the last listener must
    // never stop the turn subscription or cancel server-owned execution.
    return () => {
      if (entry.listeners.delete(listener)) this.pruneTerminalEntries()
    }
  }

  async receive(
    target: DurableChatTurnIdentity,
    message: DurableChatTurnSubscriptionMessage
  ): Promise<void> {
    const entry = this.entries.get(targetKey(target))
    if (!entry || entry.state.terminal) return

    switch (message.type) {
      case "turn_accepted": {
        if (!matchesTarget(entry.state, message)) return
        // Attach-time header status; a pending control already folded onto
        // the snapshot outranks it (see `statusWithPendingControls`).
        const status = statusWithPendingControls(message.status, entry.state.snapshot)
        if (entry.state.status !== status) {
          entry.state = { ...entry.state, status }
          this.emit(entry)
        }
        return
      }

      case "turn_event":
        if (!matchesTarget(entry.state, message)) return
        await this.receiveEvent(entry, message)
        return

      case "replay_gap":
        if (!matchesTarget(entry.state, message)) return
        await this.recover(entry)
        return

      case "turn_snapshot":
        if (!matchesTarget(entry.state, message.snapshot)) return
        if (message.snapshot.snapshotSeq < entry.state.cursor) return
        {
          const wasTerminal = entry.state.terminal
          this.replaceFromSnapshot(entry, message.snapshot)
          if (isTerminalStatus(message.snapshot.status)) {
            this.finishTerminalTransition(entry, wasTerminal)
          } else {
            this.drainBufferedEvents(entry)
          }
        }
        this.emit(entry)
        return

      case "turn_terminal":
        if (!matchesTarget(entry.state, message) || !matchesTarget(entry.state, message.snapshot)) {
          return
        }
        if (
          message.status !== message.snapshot.status ||
          message.snapshot.snapshotSeq < entry.state.cursor
        ) {
          await this.recover(entry)
          return
        }
        {
          const wasTerminal = entry.state.terminal
          this.replaceFromSnapshot(entry, message.snapshot)
          entry.state = {
            ...entry.state,
            cursor: Math.max(entry.state.cursor, message.seq),
            status: message.status,
            terminal: true,
          }
          this.finishTerminalTransition(entry, wasTerminal)
        }
        this.emit(entry)
        return
    }
  }

  /**
   * Catch every unfinished turn up NOW (tab visible again / back online,
   * issue #3354). A hidden tab's timers are throttled, so the reconnect
   * backoff, recovery retry and status poll can sit for minutes after the
   * server has finished. Per entry: skip a pending socket backoff, restart a
   * socket that already gave up (status polling stops once it connects), and
   * read the status over HTTP — `recover()` is deduped, folds only events past
   * the cursor and settles a turn that finished meanwhile.
   */
  resync(): void {
    for (const entry of [...this.entries.values()]) {
      if (entry.state.terminal) continue
      if (entry.subscriptionActive) {
        entry.subscription.reconnectNow?.()
      } else {
        entry.subscriptionActive = true
        entry.subscription.start()
      }
      void this.recover(entry)
    }
  }

  dispose(target: DurableChatTurnIdentity): void {
    const key = targetKey(target)
    const entry = this.entries.get(key)
    if (!entry) return
    this.disposeEntry(key, entry)
  }

  clear(): void {
    const entries = [...this.entries.values()]
    this.entries.clear()
    for (const entry of entries) {
      entry.listeners.clear()
      entry.bufferedEvents.clear()
      this.clearRecoveryTimer(entry)
      entry.subscriptionActive = false
      entry.subscription.stop()
    }
  }

  private async receiveEvent(entry: RegistryEntry, turnEvent: DurableChatTurnEvent): Promise<void> {
    if (turnEvent.seq <= entry.state.cursor) return
    if (turnEvent.seq === entry.state.cursor + 1) {
      this.appendEvent(entry, turnEvent)
      this.drainBufferedEvents(entry)
      this.emit(entry)
      return
    }

    this.bufferEvent(entry, turnEvent)
    await this.recover(entry)
  }

  private bufferEvent(entry: RegistryEntry, turnEvent: DurableChatTurnEvent): void {
    if (entry.bufferedEvents.has(turnEvent.seq)) return
    if (entry.bufferedEvents.size >= this.maxBufferedEvents) {
      // Retain the newest signal only. The authoritative status/snapshot fetch
      // repairs all omitted history before this frame can be applied.
      entry.bufferedEvents.clear()
    }
    entry.bufferedEvents.set(turnEvent.seq, turnEvent)
  }

  private async recover(entry: RegistryEntry): Promise<void> {
    if (!this.ownsEntry(entry) || entry.state.terminal) return
    if (entry.recovery) return entry.recovery
    this.clearRecoveryTimer(entry)

    const target: DurableChatTurnIdentity = {
      sessionId: entry.state.sessionId,
      turnId: entry.state.turnId,
      epoch: entry.state.epoch,
    }
    const afterSeq = entry.state.cursor
    let statusRequest: Promise<DurableChatTurnStatusResponse>
    try {
      statusRequest = this.loadStatus(target, afterSeq, entry.getToken())
    } catch (error) {
      statusRequest = Promise.reject(error)
    }
    const recovery = statusRequest
      .then((response) => {
        if (
          !matchesTarget(target, response) ||
          !matchesTarget(target, response.snapshot) ||
          response.status !== response.snapshot.status ||
          response.terminal !== isTerminalStatus(response.status)
        ) {
          throw new Error("Durable turn status response failed identity or status validation")
        }
        if (!this.ownsEntry(entry) || entry.state.terminal) return
        if (response.snapshot.snapshotSeq < entry.state.cursor) {
          this.completeRecovery(entry)
          return
        }

        const wasTerminal = entry.state.terminal
        // No `replayGap` means `response.snapshot` is a synthesized, EMPTY
        // projection (only `status` is real — `build_snapshot_dto`'s no-gap
        // branch). Replacing it would wipe already-reconstructed content
        // (issue #2513); advance status/terminal only and fold `events`
        // below, same as a live WS frame. A genuine gap means the backend
        // read and returned the real covering snapshot — full resync.
        if (response.replayGap) {
          this.replaceFromSnapshot(entry, response.snapshot)
        } else {
          // Besides the status, the one whole-value field a no-gap DTO carries
          // for real is the settled turn's `finalError` (#3354). Mirror the
          // status onto the snapshot too: the answer only shows its error once
          // `snapshot.status` reads failed/completed.
          const { error, status } = response.snapshot
          entry.state = {
            ...entry.state,
            status,
            terminal: response.terminal,
            snapshot: {
              ...entry.state.snapshot,
              status,
              ...(response.terminal && error ? { error } : {}),
            },
          }
        }
        for (const turnEvent of response.events) {
          if (!matchesTarget(target, turnEvent) || turnEvent.seq <= entry.state.cursor) continue
          this.bufferEvent(entry, turnEvent)
        }
        // Always drain BEFORE any terminal transition (issue #2513 finding
        // 1): `finishTerminalTransition` clears `bufferedEvents` as part of
        // settling into terminal state. Calling it first — as the previous
        // fix did — permanently discards whatever this very response just
        // buffered, silently dropping the final chunk of the answer on the
        // exact HTTP status-poll path relied on to unfreeze a viewer.
        this.drainBufferedEvents(entry)
        // Re-derive AFTER folding: the no-gap branch above copied the advisory
        // header status, which can still read `running` while the events just
        // folded (or the covering snapshot) carry a pending control (#2962).
        entry.state = {
          ...entry.state,
          status: statusWithPendingControls(entry.state.status, entry.state.snapshot),
        }
        if (response.terminal) {
          this.finishTerminalTransition(entry, wasTerminal)
        }
        this.completeRecovery(entry)
      })
      .catch(() => this.handleRecoveryFailure(entry))
      .finally(() => {
        if (entry.recovery === recovery) entry.recovery = null
        this.pruneTerminalEntries()
      })
    entry.recovery = recovery
    return recovery
  }

  private completeRecovery(entry: RegistryEntry): void {
    entry.recoveryAttempts = 0
    const shouldPoll = entry.statusPolling && !entry.state.terminal
    entry.state = { ...entry.state, reconnecting: shouldPoll }
    if (shouldPoll) this.scheduleRecovery(entry, this.statusPollIntervalMs)
    this.emit(entry)
  }

  private handleRecoveryFailure(entry: RegistryEntry): void {
    if (!this.ownsEntry(entry) || entry.state.terminal) return
    const delay = computeBackoffDelayMs(entry.recoveryAttempts, {
      base: this.baseRecoveryDelayMs,
      max: this.maxRecoveryDelayMs,
    })
    entry.recoveryAttempts += 1
    this.setReconnecting(entry, true)
    this.scheduleRecovery(entry, delay)
  }

  private scheduleRecovery(entry: RegistryEntry, delayMs: number): void {
    if (!this.ownsEntry(entry) || entry.state.terminal || entry.recoveryTimer !== null) return
    entry.recoveryTimer = setTimeout(() => {
      entry.recoveryTimer = null
      void this.recover(entry)
    }, delayMs)
  }

  private clearRecoveryTimer(entry: RegistryEntry): void {
    if (entry.recoveryTimer === null) return
    clearTimeout(entry.recoveryTimer)
    entry.recoveryTimer = null
  }

  private startStatusPolling(entry: RegistryEntry): Promise<void> {
    if (!this.ownsEntry(entry) || entry.state.terminal) return Promise.resolve()
    entry.statusPolling = true
    this.setReconnecting(entry, true)
    return this.recover(entry)
  }

  private replaceFromSnapshot(entry: RegistryEntry, turnSnapshot: DurableChatTurnSnapshot): void {
    entry.state = replacedRegistryState(entry.state, turnSnapshot)
    for (const seq of entry.bufferedEvents.keys()) {
      if (seq <= turnSnapshot.snapshotSeq) entry.bufferedEvents.delete(seq)
    }
  }

  private appendEvent(entry: RegistryEntry, turnEvent: DurableChatTurnEvent): void {
    const events = [...entry.state.events, turnEvent]
    if (events.length > this.maxRetainedEvents) {
      events.splice(0, events.length - this.maxRetainedEvents)
    }
    // Fold the event onto the live snapshot too (issue #2513 finding 4) —
    // `state.snapshot` is what every listener (and the hydration UI mirror)
    // actually renders; `state.events` alone had zero consumers.
    entry.state = foldEventOntoState(entry.state, turnEvent, events)
  }

  private drainBufferedEvents(entry: RegistryEntry): void {
    let next = entry.bufferedEvents.get(entry.state.cursor + 1)
    while (next) {
      entry.bufferedEvents.delete(next.seq)
      this.appendEvent(entry, next)
      next = entry.bufferedEvents.get(entry.state.cursor + 1)
    }
  }

  private finishTerminalTransition(entry: RegistryEntry, wasTerminal: boolean): void {
    if (!isTerminalStatus(entry.state.status)) return
    entry.statusPolling = false
    entry.bufferedEvents.clear()
    this.clearRecoveryTimer(entry)
    entry.state = { ...entry.state, reconnecting: false, terminal: true }
    if (!wasTerminal && entry.subscriptionActive) {
      entry.subscriptionActive = false
      entry.subscription.stop()
    }
    this.touchEntry(entry)
    this.pruneTerminalEntries()
  }

  private updateConnectionState(
    entry: RegistryEntry,
    connectionState: DurableChatTurnConnectionState
  ): void {
    if (!this.ownsEntry(entry) || entry.state.terminal) return
    if (connectionState === "stopped") entry.subscriptionActive = false
    if (connectionState === "connected") {
      entry.statusPolling = false
      this.clearRecoveryTimer(entry)
    }
    this.setReconnecting(entry, connectionState === "reconnecting" || entry.statusPolling)
  }

  private setReconnecting(entry: RegistryEntry, reconnecting: boolean): void {
    if (entry.state.reconnecting === reconnecting) return
    entry.state = { ...entry.state, reconnecting }
    this.emit(entry)
  }

  private ownsEntry(entry: RegistryEntry): boolean {
    return this.entries.get(targetKey(entry.state)) === entry
  }

  private touchEntry(entry: RegistryEntry): void {
    const key = targetKey(entry.state)
    if (this.entries.get(key) !== entry) return
    this.entries.delete(key)
    this.entries.set(key, entry)
  }

  private pruneTerminalEntries(): void {
    for (const [key, entry] of terminalEntriesToDispose(
      this.entries,
      this.maxRetainedTerminalTurns
    )) {
      this.disposeEntry(key, entry)
    }
  }

  private disposeEntry(key: string, entry: RegistryEntry): void {
    if (this.entries.get(key) === entry) this.entries.delete(key)
    entry.listeners.clear()
    entry.bufferedEvents.clear()
    this.clearRecoveryTimer(entry)
    entry.subscriptionActive = false
    entry.subscription.stop()
  }

  private emit(entry: RegistryEntry): void {
    const state = cloneRegistryState(entry.state)
    for (const listener of entry.listeners) listener(state)
  }
}

type ResyncSignals = {
  focusManager: { subscribe: (listener: (focused: boolean) => void) => () => void }
  onlineManager: { subscribe: (listener: (online: boolean) => void) => () => void }
}

/** Resync `registry` whenever the tab regains visibility or the browser comes
 *  back online. Uses TanStack's managers — the same signal the hydration poll
 *  already gates on — rather than a second visibility mechanism. */
export function bindDurableChatTurnResync(
  registry: { resync: () => void },
  { focusManager, onlineManager }: ResyncSignals
): () => void {
  const unsubscribeFocus = focusManager.subscribe((focused) => {
    if (focused) registry.resync()
  })
  const unsubscribeOnline = onlineManager.subscribe((online) => {
    if (online) registry.resync()
  })
  return () => {
    unsubscribeFocus()
    unsubscribeOnline()
  }
}

/** Page-lifetime transport owner; hook listeners may freely mount and unmount. */
export const durableChatTurnRegistry = new DurableChatTurnRegistry()

if (typeof window !== "undefined") {
  bindDurableChatTurnResync(durableChatTurnRegistry, { focusManager, onlineManager })
}
